import { createContext, useContext, useState, type ReactNode } from "react";

import type { CitationObject, EditAnnotation, WebCitationObject } from "@/lib/stream";

type ChatCitations = Array<CitationObject | WebCitationObject>;

/**
 * Inspector tab model — the single source of truth for the right-hand pane.
 *
 * Each pane `kind` owns its own payload type (a discriminated union on `kind`),
 * so adding or reshaping one kind never touches the others. The shell never
 * switches on `kind` inline; rendering is dispatched through the
 * pane-renderer registry (see `inspectorRegistry.ts`).
 */

/** A plain document view (no specific target within it). */
export interface DocumentTarget {
  documentId: string;
  filename: string;
  versionId?: string;
  versionNumber?: number | null;
  warning?: string;
}

/** A document opened to a specific citation (page + quote to highlight). */
export interface CitationTarget {
  documentId: string;
  filename: string;
  versionId?: string;
  versionNumber?: number | null;
  citation?: CitationObject;
  warning?: string;
}

/** A document opened to a tracked-change edit (scroll-to-edit in the docx view). */
export interface EditTarget {
  documentId: string;
  filename: string;
  versionId?: string;
  versionNumber?: number | null;
  edit?: EditAnnotation;
  warning?: string;
}

/** Citations list shown in the inspector. Empty when opened from the new-tab selector. */
export interface CitationsListTarget {
  citations: Array<CitationObject | WebCitationObject>;
  /** The selected assistant message, when the tab is opened from a completed answer. */
  turnId?: string;
}

/** Cross-turn worklog timeline (data comes from WorklogContext, not payload). */
export type WorklogTarget = Record<string, never>;

/** Source viewer showing all documents referenced in the current chat. */
export type SourceViewerTarget = Record<string, never>;

export type InspectorTab =
  | { kind: "document"; id: string; pinned: boolean; title: string; payload: DocumentTarget }
  | { kind: "citation"; id: string; pinned: boolean; title: string; payload: CitationTarget }
  | { kind: "edit"; id: string; pinned: boolean; title: string; payload: EditTarget }
  | { kind: "citations"; id: string; pinned: boolean; title: string; payload: CitationsListTarget }
  | { kind: "worklog"; id: string; pinned: boolean; title: string; payload: WorklogTarget }
  | { kind: "sources"; id: string; pinned: boolean; title: string; payload: SourceViewerTarget };

export type InspectorTabKind = InspectorTab["kind"];

/** Distributes `Omit` across each union member so the discriminant is preserved. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** `openTab` input: a tab without the required `pinned` flag (defaults to unpinned). */
export type OpenTabInput = DistributiveOmit<InspectorTab, "pinned"> & { pinned?: boolean };

export interface InspectorApi {
  tabs: InspectorTab[];
  activeTabId: string | null;
  /** Monotonically increasing counter bumped on every `openTab` call. */
  openSeq: number;
  /** Aggregate citations from the current chat (set by ChatSurface). */
  chatCitations: ChatCitations;
  /** Open (or re-target, deduping by `id`) a tab and activate it. */
  openTab(tab: OpenTabInput): void;
  /** Close a tab; no-op for pinned tabs. */
  closeTab(id: string): void;
  setActiveTab(id: string): void;
  /** Deselect the active tab to show the new-tab selector. */
  showSelector(): void;
  /** Close every closable tab (pinned tabs are kept). */
  closeAll(): void;
  /** Update the chat-level citations (called by the chat surface). */
  setChatCitations(citations: ChatCitations): void;
}

interface InspectorState {
  tabs: InspectorTab[];
  activeTabId: string | null;
  openSeq: number;
}

const InspectorContext = createContext<InspectorApi | null>(null);

export function InspectorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InspectorState>({ tabs: [], activeTabId: null, openSeq: 0 });
  const [chatCitations, setChatCitations] = useState<ChatCitations>([]);

  const openTab = (input: OpenTabInput) => {
    setState((prev) => {
      const existingIndex = prev.tabs.findIndex((tab) => tab.id === input.id);
      const pinned = input.pinned ?? (existingIndex >= 0 ? prev.tabs[existingIndex].pinned : false);
      const nextTab = { ...input, pinned } as InspectorTab;
      const tabs =
        existingIndex >= 0
          ? prev.tabs.map((tab, index) => (index === existingIndex ? nextTab : tab))
          : [...prev.tabs, nextTab];
      return { tabs, activeTabId: input.id, openSeq: prev.openSeq + 1 };
    });
  };

  const closeTab = (id: string) => {
    setState((prev) => {
      const index = prev.tabs.findIndex((tab) => tab.id === id);
      if (index < 0 || prev.tabs[index].pinned) {
        return prev;
      }
      const tabs = prev.tabs.filter((tab) => tab.id !== id);
      let activeTabId = prev.activeTabId;
      if (prev.activeTabId === id) {
        const neighbor = tabs[index] ?? tabs[index - 1] ?? null;
        activeTabId = neighbor ? neighbor.id : null;
      }
      return { ...prev, tabs, activeTabId };
    });
  };

  const setActiveTab = (id: string) => {
    setState((prev) => (prev.tabs.some((tab) => tab.id === id) ? { ...prev, activeTabId: id } : prev));
  };

  const closeAll = () => {
    setState((prev) => {
      const tabs = prev.tabs.filter((tab) => tab.pinned);
      const activeTabId = tabs.some((tab) => tab.id === prev.activeTabId) ? prev.activeTabId : (tabs[0]?.id ?? null);
      return { ...prev, tabs, activeTabId };
    });
  };

  const showSelector = () => {
    setState((prev) => ({ ...prev, activeTabId: null }));
  };

  const api: InspectorApi = { tabs: state.tabs, activeTabId: state.activeTabId, openSeq: state.openSeq, chatCitations, openTab, closeTab, setActiveTab, showSelector, closeAll, setChatCitations };

  return <InspectorContext.Provider value={api}>{children}</InspectorContext.Provider>;
}

export function useInspector(): InspectorApi {
  const context = useContext(InspectorContext);
  if (!context) {
    throw new Error("useInspector must be used within InspectorProvider");
  }
  return context;
}
